import * as fs from 'fs';
import * as path from 'path';

const SOURCE_DIRS = ['src', 'components', 'app', 'lib'] as const;

const INTRO_PAGE_CANDIDATES = [
  'src/components/IntroPage.tsx',
  'components/IntroPage.tsx',
  'app/components/IntroPage.tsx',
] as const;

const SURVEY_PAGE_CANDIDATES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
] as const;

const END_PAGE_CANDIDATES = [
  'src/components/EndPage.tsx',
  'components/EndPage.tsx',
  'app/components/EndPage.tsx',
] as const;

const QUESTION_CANDIDATES = [
  'src/components/Question.tsx',
  'components/Question.tsx',
  'app/components/Question.tsx',
] as const;

const MATRIX_SCALE_FILES = [
  'CsatMatrixScale.tsx',
  'SliderMatrixScale.tsx',
  'LikertMatrixScale.tsx',
] as const;

const MATRIX_RELATED_FILES = [
  ...MATRIX_SCALE_FILES,
  'MatrixDropdown.tsx',
] as const;

const AGENT_AUTHORED_STYLE_FILES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
  'src/globals.css',
  'src/index.css',
  'src/app/globals.css',
  'app/globals.css',
  'src/app/layout.tsx',
  'app/layout.tsx',
] as const;

const HARDCODED_HEX = /#[0-9A-Fa-f]{3,8}\b/;
const HARDCODED_RGB = /\brgba?\s*\(/;

function collectSourceFiles(projectRoot: string): string[] {
  const files: string[] = [];
  for (const dir of SOURCE_DIRS) {
    const abs = path.join(projectRoot, dir);
    if (!fs.existsSync(abs)) continue;
    walk(abs, files);
  }
  return files;
}

function walk(dir: string, out: string[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    const abs = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      if (entry.name === 'node_modules' || entry.name === '.next' || entry.name === 'dist') {
        continue;
      }
      walk(abs, out);
    } else if (/\.(tsx?|jsx?|mjs|cjs)$/.test(entry.name)) {
      out.push(abs);
    }
  }
}

function findFirstExisting(projectRoot: string, candidates: readonly string[]): string | null {
  for (const rel of candidates) {
    const abs = path.join(projectRoot, rel);
    if (fs.existsSync(abs)) return rel;
  }
  return null;
}

function isAllowedStyleLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*')) return true;
  if (trimmed.includes('var(--cfm-')) return true;
  if (/url\s*\(#/.test(trimmed)) return true;
  return false;
}

export function scanHardcodedStyleIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const rel of AGENT_AUTHORED_STYLE_FILES) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;

    const content = fs.readFileSync(abs, 'utf8');
    const lines = content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (isAllowedStyleLine(line)) continue;
      if (HARDCODED_HEX.test(line) || HARDCODED_RGB.test(line)) {
        issues.push(`${rel}:${i + 1}: hardcoded color — use survey-ui-config.json + var(--cfm-*)`);
      }
    }
  }

  return issues;
}

export function scanIntroStartIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  const introRel = findFirstExisting(projectRoot, INTRO_PAGE_CANDIDATES);

  if (!introRel) {
    issues.push(
      'IntroPage.tsx missing — author an intro page with a Start button that dispatches onAction START',
    );
    return issues;
  }

  const introContent = fs.readFileSync(path.join(projectRoot, introRel), 'utf8');

  if (!/data-cfm-btn-start/.test(introContent)) {
    issues.push(`${introRel}: Start button missing data-cfm-btn-start`);
  }
  if (!/onClick=\{onStart\}|onClick=\{\s*onStart\s*\}/.test(introContent)) {
    issues.push(`${introRel}: Start button must use onClick={onStart}`);
  }
  if (/\{showStart &&|showStart &&/.test(introContent)) {
    issues.push(`${introRel}: conditional Start (showStart) — always render Start button`);
  }

  const surveyRel = findFirstExisting(projectRoot, SURVEY_PAGE_CANDIDATES);
  if (!surveyRel) return issues;

  const surveyContent = fs.readFileSync(path.join(projectRoot, surveyRel), 'utf8');
  const hasIntroPhase = /phase\.kind\s*===\s*['"]intro['"]|kind\s*===\s*['"]intro['"]/.test(
    surveyContent,
  );

  if (hasIntroPhase && !/<IntroPage/.test(surveyContent)) {
    issues.push(
      `${surveyRel}: intro phase must render <IntroPage /> — do not inline header/description only`,
    );
  }
  if (/<IntroPage/.test(surveyContent) && !/type:\s*['"]START['"]/.test(surveyContent)) {
    issues.push(`${surveyRel}: IntroPage must wire onAction({ type: 'START' })`);
  }

  return issues;
}

function endPageFiltersEmailCtAs(content: string): boolean {
  if (/endPageCtas\s*\(/.test(content)) return true;
  if (/sendEmailResponse,\s*resumptionNotification,\s*reopenNotification/.test(content)) {
    return true;
  }
  return false;
}

export function scanEndPageCtaIssues(projectRoot: string): string[] {
  const endRel = findFirstExisting(projectRoot, END_PAGE_CANDIDATES);
  if (!endRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, endRel), 'utf8');
  if (!/<SpecialPageCtaSection/.test(content)) return [];

  if (endPageFiltersEmailCtAs(content)) return [];

  if (/ctas=\{[^}]*page\.ctas[^}]*\}/.test(content)) {
    return [
      `${endRel}: EndPage must filter email CTAs via endPageCtas(page.ctas) before SpecialPageCtaSection — see 15-end-page.md`,
    ];
  }

  return [];
}

export interface MatrixVariantPlanRow {
  questionId: string;
  type: string;
  displayStyle: string;
  gridLayout: string;
  statementLayout: string;
  transposeTable: string;
  selectionMode: string;
  hasNotApplicableOption: string;
  branchOn?: string;
  scaleAnchorLabelCount?: string;
}

function parseTruthyFlag(value: string | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === 'yes';
}

export function readMatrixVariantsFromPlan(projectRoot: string): MatrixVariantPlanRow[] {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return [];

  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3c. Matrix variant inventory')[1];
  if (!section) return [];

  const tablePart = section.split('##')[0] ?? '';
  const lines = tablePart.split('\n').filter((l) => l.trim().startsWith('|'));
  const rows: MatrixVariantPlanRow[] = [];

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    // Skip markdown separator rows (only pipes, dashes, colons, whitespace) — including
    // multi-column separators like `|------|------|` that a single-dash regex misses.
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no matrix questions in fetch)')) continue;

    const cells = line
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length < 6) continue;

    if (cells.length >= 10) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
        branchOn: cells[8],
        scaleAnchorLabelCount: cells[9],
      });
      continue;
    }

    if (cells.length >= 9) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
        branchOn: cells[8],
      });
      continue;
    }

    if (cells.length >= 8) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
      });
      continue;
    }

    // Legacy §3c (6 columns, no gridLayout / hasNA)
    rows.push({
      questionId: cells[0],
      type: cells[1],
      displayStyle: cells[2],
      gridLayout: '—',
      statementLayout: cells[3],
      transposeTable: cells[4],
      selectionMode: cells[5],
      hasNotApplicableOption: cells[6] ?? '—',
    });
  }

  return rows;
}

function readScaleValuesContent(projectRoot: string): string {
  for (const file of collectSourceFiles(projectRoot)) {
    if (path.basename(file) === 'scaleValues.ts') {
      return fs.readFileSync(file, 'utf8');
    }
  }
  return '';
}

function matrixScaleFileForType(type: string): string | undefined {
  if (type === 'CFM_MATRIX') return 'LikertMatrixScale.tsx';
  if (type === 'CSAT_MATRIX' || type === 'RATING_MATRIX') return 'CsatMatrixScale.tsx';
  if (type === 'SLIDER_MATRIX') return 'SliderMatrixScale.tsx';
  return undefined;
}

function extractFunctionBody(content: string, name: string): string {
  const re = new RegExp(`function\\s+${name}\\b[\\s\\S]*?(?=\\nfunction\\s+\\w|$)`);
  return content.match(re)?.[0] ?? '';
}

function stripHoverTooltipBlocks(source: string): string {
  return source.replace(/\{isHovered\s*&&[\s\S]*?\}\s*\)\s*\}/g, '');
}

function numberedCellUsesColLabel(content: string): boolean {
  for (const fn of ['CsatMatrixGrid', 'CsatMatrixCarousel']) {
    const body = extractFunctionBody(content, fn);
    if (!body || !/\bisNumbered\b/.test(body)) continue;

    const stripped = stripHoverTooltipBlocks(body);
    if (!/displayIdx\s*\+\s*1/.test(body) && /col\.label/.test(stripped)) {
      return true;
    }

    if (
      /isNumbered[\s\S]{0,3500}col\.label/.test(stripped) &&
      (/<\/button>[\s\S]{0,600}col\.label/.test(stripped) ||
        /dangerouslySetInnerHTML[\s\S]{0,80}col\.label/.test(stripped))
    ) {
      return true;
    }
  }

  return false;
}

function anchorUsesJustifyBetweenWithoutAbsolute(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content)) return false;

  const anchorBlocks = content.match(/scaleAnchorLabels[\s\S]{0,1800}/g);
  if (!anchorBlocks?.length) return false;

  return anchorBlocks.some((block) => {
    const hasJustify =
      /justify-between/.test(block) ||
      /justifyContent:\s*['"]space-between['"]/.test(block);
    const hasAbsolute = /position:\s*['"]absolute['"]/.test(block);
    return hasJustify && !hasAbsolute;
  });
}

function likertMissingNonNaScaleColumns(content: string): boolean {
  if (!/scaleColumns|gridCols/.test(content)) return false;
  return !/nonNaScaleColumns/.test(content);
}

function likertNaInGridLoop(content: string): boolean {
  if (/nonNaScaleColumns/.test(content)) return false;
  return /gridCols\.map|scaleColumns\.map/.test(content);
}

function csatCarouselDuplicateLabels(content: string): boolean {
  const carousel = extractFunctionBody(content, 'CsatMatrixCarousel');
  if (!carousel) return false;
  if (!/\bisNumbered\b|\bisEmoji\b|\bisStar\b/.test(carousel)) return false;
  const stripped = stripHoverTooltipBlocks(carousel);
  return (
    /<\/button>[\s\S]{0,600}col\.label/.test(stripped) ||
    /dangerouslySetInnerHTML[\s\S]{0,80}col\.label/.test(stripped)
  );
}

function csatNaHeaderGapWhenAnchors(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content) || !/hasNotApplicable/.test(content)) return false;

  const showLabelsBlock = content.match(/showLabels[\s\S]{0,2500}/);
  if (!showLabelsBlock) return false;

  const block = showLabelsBlock[0];
  const reservesNaSlot = /hasNotApplicable[\s\S]{0,120}flexShrink:\s*0/.test(block);
  const hasNaHeaderText = /['"]N\/A['"]|Not Applicable|>\s*N\/A\s*</.test(block);
  return reservesNaSlot && !hasNaHeaderText;
}

function likertBipolarStructureWeak(content: string): boolean {
  if (!/oppositeStatementText/.test(content)) return true;
  if (/flex-1|flex:\s*1\b|flex:\s*['"]1['"]|w-1\/4/.test(content)) return false;
  if (/0\s*0\s*25%|flex:\s*isBipolar/.test(content)) {
    return bipolarGridOnlyWithoutMiddleFlex(content);
  }
  return true;
}

/** Bipolar using grid `0 0 25%` without a flex-1 middle scale zone. */
function bipolarGridOnlyWithoutMiddleFlex(content: string): boolean {
  if (!/0\s*0\s*25%/.test(content)) return false;
  return !/flex-1|flex:\s*1\b|flex:\s*['"]1['"]/.test(content);
}

function csatNaInGridLoop(content: string): boolean {
  if (/nonNaScaleColumns/.test(content)) return false;
  return /scaleColumns\.map/.test(content);
}

function ratingGraphicsUsesNumericIndex(content: string): boolean {
  if (!/isGraphics|displayStyle\s*===\s*['"]graphics['"]/.test(content)) return false;
  if (!/CustomSliderTrack/.test(content)) return false;
  return (
    /typeof\s+\w+\s*===\s*['"]number['"]/.test(content) ||
    /value=\{\s*\w+\s*\}/.test(content) && /rowAnswers\[|answers\[/.test(content) && !/findIndex/.test(content)
  );
}

function graphicsLabelsInsideStatementLoop(content: string): boolean {
  if (!/isGraphics|displayStyle\s*===\s*['"]graphics['"]/.test(content)) return false;
  if (/GraphicsColumnLabelsRow/.test(content)) {
    return /statementRows\.map[\s\S]{0,2500}GraphicsColumnLabelsRow/.test(content);
  }
  return /statementRows\.map[\s\S]{0,2000}scaleColumns\.map[\s\S]{0,1200}(col\.label|dangerouslySetInnerHTML)/.test(
    content,
  );
}

function matrixScaleFlexOnlyWithoutGrid(content: string): boolean {
  const hasGrid =
    /gridTemplateColumns/.test(content) ||
    /repeat\s*\(\s*\$\{?\s*m\s*\}?/.test(content) ||
    /repeat\s*\([^)]*,\s*1fr\s*\)/.test(content);
  if (hasGrid) return false;
  const hasFlexCells =
    /(?:className=['"][^'"]*gap-|gap:\s*\d+|justify-between|justifyContent:\s*['"]space-between['"])/.test(
      content,
    );
  const hasCellLoop = /scaleColumns\.map|gridCols\.map|nonNaScaleColumns\(/.test(content);
  return hasFlexCells && hasCellLoop;
}

function pushAutoFitIssue(
  issues: string[],
  name: string,
  variantId: string | undefined,
  strict: boolean,
): void {
  const msg = variantId
    ? `Matrix carousel parity (${variantId}): auto-fit minmax in index map — use shared gridTemplateColumns — see layout-cookbook/shared.md`
    : `Matrix carousel parity: auto-fit minmax in ${name} — header/cell index map may drift — see layout-cookbook/shared.md`;
  if (strict) {
    issues.push(msg);
  } else {
    issues.push(`${MATRIX_NEAR_MISS_PREFIX} ${msg}`);
  }
}

function likertCarouselLacksBipolar(content: string): boolean {
  const carousel = extractFunctionBody(content, 'LikertMatrixCarouselLayout');
  if (!carousel) return false;
  return !/oppositeStatementText|isBipolar/.test(carousel);
}

function matrixFileHasAutoFitMinmax(content: string): boolean {
  return /auto-fit\s+minmax|repeat\s*\(\s*auto-fit/i.test(content);
}

function carouselMissingNavSignals(content: string): boolean {
  if (!/Carousel|gridLayout\s*===\s*['"]carousel['"]/.test(content)) return false;
  return !/activeCarouselIndex|setActiveCarousel|Prev|Next|\d+\s*\/\s*\d+|carouselIndex/i.test(
    content,
  );
}

export const MATRIX_NEAR_MISS_PREFIX = 'NEAR-MISS:';

export function isMatrixLayoutNearMiss(issue: string): boolean {
  return issue.startsWith(MATRIX_NEAR_MISS_PREFIX);
}

export function partitionMatrixLayoutIssues(issues: string[]): {
  blocking: string[];
  nearMisses: string[];
} {
  const blocking: string[] = [];
  const nearMisses: string[] = [];
  for (const issue of issues) {
    if (isMatrixLayoutNearMiss(issue)) nearMisses.push(issue);
    else blocking.push(issue);
  }
  return { blocking, nearMisses };
}

function readMatrixFileContents(projectRoot: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_RELATED_FILES as readonly string[]).includes(basename)) continue;
    out.set(basename, fs.readFileSync(file, 'utf8'));
  }
  return out;
}

function questionDispatcherContent(projectRoot: string): string {
  const rel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!rel) return '';
  return fs.readFileSync(path.join(projectRoot, rel), 'utf8');
}

export interface ScanMatrixLayoutOptions {
  /** When true, index-map anti-patterns fail instead of NEAR-MISS (HOOK 3 / verify-layout). */
  strict?: boolean;
}

export function scanMatrixLayoutIssues(
  projectRoot: string,
  options: ScanMatrixLayoutOptions = {},
): string[] {
  const strict = options.strict ?? false;
  const issues: string[] = [];
  const files = readMatrixFileContents(projectRoot);
  if (files.size === 0) return issues;

  const matrixBlob = [...files.entries()]
    .map(([name, content]) => `// ${name}\n${content}`)
    .join('\n');

  for (const [name, content] of files) {
    if ((MATRIX_SCALE_FILES as readonly string[]).includes(name)) {
      if (!/<select[\s>]/i.test(content) && !/<details[\s>]/i.test(content)) continue;
      issues.push(
        `${name}: matrix dropdown must use custom MatrixDropdown — no native <select> or <details>`,
      );
    }
  }

  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasBipolar = variants.some(
    (r) => r.type === 'CFM_MATRIX' && /bipolar/i.test(r.statementLayout),
  );
  const hasTranspose = variants.some((r) => /true/i.test(r.transposeTable));
  const hasRatingGraphics = variants.some(
    (r) => r.type === 'RATING_MATRIX' && /graphics/i.test(r.displayStyle),
  );
  const hasDropdownStyle = variants.some((r) => {
    if (r.type === 'CFM_MATRIX') {
      return /dropdown/i.test(r.gridLayout);
    }
    return /dropdown|selectbox/i.test(r.displayStyle);
  });

  if (hasBipolar) {
    const likert = files.get('LikertMatrixScale.tsx');
    if (likert && likertBipolarStructureWeak(likert)) {
      issues.push(
        'Matrix bipolar layout: LikertMatrixScale.tsx — 3-zone row with oppositeStatementText in right column (see layout-cookbook/cfm_matrix.md)',
      );
    }
    if (likert && likertCarouselLacksBipolar(likert)) {
      issues.push(
        `${MATRIX_NEAR_MISS_PREFIX} Matrix bipolar layout: carousel branch lacks bipolar 3-zone — see layout-cookbook/shared.md`,
      );
    }
  }

  if (hasTranspose) {
    if (!/transposeTable/.test(matrixBlob)) {
      issues.push(
        'Matrix scale components: §3c has transposeTable — branch with swapped row/col iteration (see layout-cookbook.md)',
      );
    }
  }

  if (hasRatingGraphics) {
    const csat = files.get('CsatMatrixScale.tsx') ?? '';
    if (!/CustomSliderTrack/.test(csat)) {
      issues.push(
        'CsatMatrixScale.tsx: §3c has RATING_MATRIX graphics — use CustomSliderTrack per row, not cell button grid',
      );
    }
    if (csat && ratingGraphicsUsesNumericIndex(csat)) {
      issues.push(
        'Matrix graphics value: CsatMatrixScale.tsx must use findIndex from stored column id — not typeof number or raw answer index — see layout-cookbook/rating_matrix.md',
      );
    }
    if (csat && graphicsLabelsInsideStatementLoop(csat)) {
      issues.push(
        'Matrix graphics labels: CsatMatrixScale.tsx must render GraphicsColumnLabelsRow once outside statementRows.map — see layout-cookbook/rating_matrix.md',
      );
    }
    const question = questionDispatcherContent(projectRoot);
    if (question && /RATING_MATRIX[\s\S]{0,400}SliderMatrixScale/.test(question)) {
      issues.push(
        'Question.tsx: RATING_MATRIX graphics must use CsatMatrixScale + CustomSliderTrack — not SliderMatrixScale',
      );
    }
  }

  if (hasDropdownStyle) {
    if (!/MatrixDropdown/.test(matrixBlob)) {
      issues.push(
        'Matrix components: §3c has dropdown/selectbox displayStyle — use MatrixDropdown per row',
      );
    }
  }

  const scaleValuesContent = readScaleValuesContent(projectRoot);
  const scaleValuesHasNonNa = /nonNaScaleColumns/.test(scaleValuesContent);
  const matrixScalesUseNonNa = [...files.entries()]
    .filter(([name]) => (MATRIX_SCALE_FILES as readonly string[]).includes(name))
    .some(([, content]) => /nonNaScaleColumns/.test(content));

  if (!scaleValuesHasNonNa || !matrixScalesUseNonNa) {
    issues.push(
      'Matrix label index: nonNaScaleColumns required in scaleValues.ts and matrix scale components — see layout-cookbook/shared.md',
    );
  }

  const likertContent = files.get('LikertMatrixScale.tsx') ?? '';
  const csatContent = files.get('CsatMatrixScale.tsx') ?? '';
  if (likertContent && likertMissingNonNaScaleColumns(likertContent)) {
    issues.push(
      'Matrix label index: LikertMatrixScale.tsx must use nonNaScaleColumns for gridCols/scaleColumns — see layout-cookbook/shared.md',
    );
  }
  if (likertContent && likertNaInGridLoop(likertContent)) {
    issues.push(
      'Matrix N/A trailing: LikertMatrixScale.tsx maps full scaleColumns/gridCols — use nonNaScaleColumns + trailing N/A slot — see layout-cookbook/cfm_matrix.md',
    );
  }
  if (csatContent && csatNaInGridLoop(csatContent)) {
    issues.push(
      'Matrix N/A trailing: CsatMatrixScale.tsx maps full scaleColumns — use nonNaScaleColumns + trailing N/A slot — see layout-cookbook/csat_matrix.md',
    );
  }

  const hasMultiColumnMatrix = variants.some(
    (v) => v.type !== 'SLIDER_MATRIX' && !/graphics/i.test(v.displayStyle),
  );

  for (const [name, content] of files) {
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(name)) continue;
    if (matrixFileHasAutoFitMinmax(content)) {
      const carouselVariant = variants.find(
        (v) => /carousel/i.test(v.gridLayout) && matrixScaleFileForType(v.type) === name,
      );
      if (carouselVariant) {
        pushAutoFitIssue(issues, name, carouselVariant.questionId, strict);
      } else {
        issues.push(
          `${MATRIX_NEAR_MISS_PREFIX} Matrix carousel parity: auto-fit minmax in ${name} — header/cell index map may drift — see layout-cookbook/shared.md`,
        );
      }
    }
    if (strict && hasMultiColumnMatrix && matrixScaleFlexOnlyWithoutGrid(content)) {
      issues.push(
        `Matrix grid skeleton: ${name} uses flex gap/justify-between without gridTemplateColumns repeat(m, 1fr) — see layout-cookbook/shared.md`,
      );
    }
    if (carouselMissingNavSignals(content)) {
      issues.push(
        `${MATRIX_NEAR_MISS_PREFIX} Matrix carousel chrome: ${name} missing row navigation/position indicator — see layout-cookbook/shared.md`,
      );
    }
  }

  if (hasBipolar && strict) {
    const likert = files.get('LikertMatrixScale.tsx');
    if (likert && bipolarGridOnlyWithoutMiddleFlex(likert)) {
      issues.push(
        'Matrix bipolar layout: LikertMatrixScale.tsx uses grid 0 0 25% without flex-1 middle zone — use 25% | flex-1 | 25% — see layout-cookbook/cfm_matrix.md',
      );
    }
  }

  if (csatContent && csatCarouselDuplicateLabels(csatContent)) {
    issues.push(
      'Matrix label index: CsatMatrixScale carousel renders col.label below emoji/star/numbered cells — see layout-cookbook/csat_matrix.md',
    );
  }
  if (csatContent && csatNaHeaderGapWhenAnchors(csatContent)) {
    issues.push(
      'Matrix N/A trailing: CsatMatrixScale scaleAnchorLabels row reserves N/A spacer without N/A header text — see layout-cookbook/csat_matrix.md',
    );
  }
  if (csatContent && numberedCellUsesColLabel(csatContent)) {
    issues.push(
      'Matrix label index: CsatMatrixScale numbered cells must use displayIdx+1 badge — not col.label in cell/carousel — see layout-cookbook/csat_matrix.md',
    );
  }
  if (
    likertContent &&
    /oppositeStatementText/.test(likertContent) &&
    !/0\s*0\s*25%|flex:\s*isBipolar/.test(likertContent)
  ) {
    issues.push(
      'Matrix bipolar layout: LikertMatrixScale.tsx references oppositeStatementText but lacks 3-zone right column — see layout-cookbook/cfm_matrix.md',
    );
  }

  for (const variant of variants) {
    if (parseTruthyFlag(variant.hasNotApplicableOption)) {
      const hasNaPattern =
        /hasNotApplicableOption/.test(matrixBlob) &&
        (/'N\/A'|Not Applicable|hasNotApplicableOption\s*&&/.test(matrixBlob));
      if (!hasNaPattern) {
        issues.push(
          `Matrix N/A trailing (${variant.questionId}): hasNA in §3c — N/A slot in header and every row — see layout-cookbook/csat_matrix.md`,
        );
      }
    }

    if (/carousel/i.test(variant.gridLayout)) {
      const scaleFile = matrixScaleFileForType(variant.type);
      const scaleContent = scaleFile ? files.get(scaleFile) ?? '' : '';
      const hasCarouselBranch =
        /carousel|Carousel|gridLayout\s*===\s*['"]carousel['"]/.test(scaleContent);
      if (!hasCarouselBranch) {
        issues.push(
          `Matrix carousel parity (${variant.questionId}): §3c gridLayout=carousel — header + cell loops share same column index map — see layout-cookbook/cfm_matrix.md`,
        );
      }
    }

    if (/numbered/i.test(variant.displayStyle) && variant.type !== 'SLIDER_MATRIX') {
      const csat = files.get('CsatMatrixScale.tsx') ?? '';
      if (numberedCellUsesColLabel(csat)) {
        issues.push(
          `Matrix label index (${variant.questionId}): numbered cells must use displayIdx+1 badge — not col.label — see layout-cookbook/csat_matrix.md`,
        );
      }
      if (
        csat &&
        /isStandardRadio/.test(csat) &&
        !/isNumbered/.test(csat) &&
        !/CsatMatrixGrid/.test(csat)
      ) {
        issues.push(
          `Matrix label index (${variant.questionId}): numbered §3c must route to grid/carousel with isNumbered — not vertical list only — see layout-cookbook/csat_matrix.md`,
        );
      }
    }
  }

  const likertContentForAnchor = files.get('LikertMatrixScale.tsx') ?? '';
  if (anchorUsesJustifyBetweenWithoutAbsolute(csatContent)) {
    issues.push(
      'Matrix label index: scaleAnchorLabels anchor row uses justify-between without position:absolute — use % anchors — see layout-cookbook/shared.md',
    );
  }
  if (anchorUsesJustifyBetweenWithoutAbsolute(likertContentForAnchor)) {
    issues.push(
      'Matrix label index: scaleAnchorLabels anchor row uses justify-between without position:absolute — use % anchors — see layout-cookbook/shared.md',
    );
  }

  return issues;
}

export function scanMatrixLayoutIssuesStrict(projectRoot: string): string[] {
  return scanMatrixLayoutIssues(projectRoot, { strict: true });
}

/**
 * Fail-closed guard for the §3c enforcement chain.
 *
 * The structural matrix checks (bipolar / transpose / graphics / dropdown) are all derived
 * from `implementation_plan.md` §3c. If matrix component files exist but §3c is empty, those
 * checks silently no-op. At the Phase-6 gate (full `verify-build`, run AFTER `sync-from-fetch`)
 * an empty §3c means the agent skipped `sync-from-fetch` or never populated the inventory — so
 * the layout checks never ran. Surface that as a blocking issue instead of passing silently.
 *
 * Only meaningful post-sync: do NOT call this from `--fast` (which legitimately runs before
 * `sync-from-fetch`).
 */
export function scanMatrixInventoryGap(projectRoot: string): string[] {
  const files = readMatrixFileContents(projectRoot);
  if (files.size === 0) return [];

  const variants = readMatrixVariantsFromPlan(projectRoot);
  if (variants.length > 0) return [];

  return [
    'Matrix component files exist but implementation_plan.md §3c is empty — run `npx cfm-sdk sync-from-fetch` and fill §3c, then re-verify (matrix layout checks depend on §3c). See layout-cookbook.md.',
  ];
}

export function scanMatrixStatementIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(basename)) continue;

    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);
    if (!/shouldShowMatrixStatementLabels/.test(content)) {
      issues.push(
        `${rel}: must call shouldShowMatrixStatementLabels(question) for single- vs multi-row statement labels`,
      );
    }
  }

  return issues;
}

export function readMcqOtherFlagFromPlan(projectRoot: string): boolean | null {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return null;

  const content = fs.readFileSync(planPath, 'utf8');
  const match = content.match(/\*\*MCQ Other option in fetch:\*\*\s*(yes|no)/i);
  if (!match) return null;
  return match[1].toLowerCase() === 'yes';
}

export function readMcqOtherPickersFlagFromPlan(projectRoot: string): boolean | null {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return null;

  const content = fs.readFileSync(planPath, 'utf8');
  const match = content.match(/\*\*MCQ Other phone\/postal\/date pickers:\*\*\s*(yes|no)/i);
  if (!match) return null;
  return match[1].toLowerCase() === 'yes';
}

function stripLineComments(content: string): string {
  return content
    .split('\n')
    .map((line) => line.replace(/\/\/.*$/, '').replace(/\/\*[\s\S]*?\*\//g, ''))
    .join('\n');
}

function findComponentFile(projectRoot: string, basename: string): string | null {
  for (const file of collectSourceFiles(projectRoot)) {
    if (path.basename(file) === basename) return file;
  }
  return null;
}

function readPackageJsonContent(projectRoot: string): string {
  const pkgPath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(pkgPath)) return '';
  return fs.readFileSync(pkgPath, 'utf8');
}

/** FAIL when CFM component branches on displayStyle (field does not exist on type). */
export function scanLikertDisplayStyleMisuse(projectRoot: string): string[] {
  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
    'app/components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const stripped = stripLineComments(content);
  if (/\bdisplayStyle\b/.test(stripped)) {
    return [
      `${likertRel}: CFM_MATRIX has no displayStyle — use question.gridLayout === 'dropdown' — see field-binding-map.md`,
    ];
  }
  return [];
}

/** FAIL when CsatStarIcons uses NPS-style half-fill anti-pattern. */
export function scanCsatStarIconAntiPattern(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasStar =
    variants.some((v) => v.type === 'CSAT_MATRIX' && /star/i.test(v.displayStyle)) ||
    variants.length === 0;

  if (!hasStar) return [];

  const iconsPath = findComponentFile(projectRoot, 'icons.tsx');
  if (!iconsPath) return [];

  const content = fs.readFileSync(iconsPath, 'utf8');
  if (/index\s*<\s*Math\.ceil|ceil\s*\(\s*total\s*\/\s*2\s*\)/.test(content)) {
    return [
      `${path.relative(projectRoot, iconsPath)}: CsatStarIcons must use filled === selected only — not NPS half-fill — see client-lib-folder.md`,
    ];
  }
  return [];
}

/** FAIL when fetch requires MCQ Other pickers but client uses plain inputs. */
export function scanMcqOtherPickerIssues(projectRoot: string): string[] {
  if (readMcqOtherPickersFlagFromPlan(projectRoot) !== true) return [];

  const issues: string[] = [];
  const countryRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherCountrySelect.tsx',
    'components/McqOtherCountrySelect.tsx',
  ]);
  const dateRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherDatePicker.tsx',
    'components/McqOtherDatePicker.tsx',
  ]);
  const inputRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherInput.tsx',
    'components/McqOtherInput.tsx',
  ]);

  if (!countryRel) {
    issues.push(
      'McqOtherCountrySelect.tsx missing — survey has PHONE_NUMBER/POSTAL_CODE Other — see 21-mcq-other-input.md',
    );
  }
  if (!dateRel) {
    issues.push('McqOtherDatePicker.tsx missing — survey has DATE Other — see 21-mcq-other-input.md');
  }

  const pkg = readPackageJsonContent(projectRoot);
  if (!/react-international-phone/.test(pkg)) {
    issues.push('package.json: missing react-international-phone — required for MCQ Other phone/postal');
  }
  if (!/react-datepicker/.test(pkg)) {
    issues.push('package.json: missing react-datepicker — required for MCQ Other date');
  }

  if (inputRel) {
    const inputContent = fs.readFileSync(path.join(projectRoot, inputRel), 'utf8');
    if (/type\s*=\s*["']date["']/i.test(inputContent)) {
      issues.push(`${inputRel}: DATE Other must use McqOtherDatePicker — not type="date"`);
    }
    if (/<select[\s>]/i.test(inputContent) && /country/i.test(inputContent)) {
      issues.push(
        `${inputRel}: use McqOtherCountrySelect for country — not plain <select> — see layout-cookbook/mcq.md`,
      );
    }
  }

  return issues;
}

function parseAnchorLabelCount(value: string | undefined): number {
  if (!value || value.trim() === '—') return 0;
  const n = parseInt(value.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function surveyPlanHasType(projectRoot: string, type: string): boolean {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return false;
  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3b.')[1]?.split('##')[0] ?? content;
  return (
    new RegExp(`\\|\\s*${type}\\s*\\|`).test(section) ||
    section.includes(`\`${type}\``) ||
    section.includes(type)
  );
}

function extractLikertCarouselBlock(content: string): string {
  const fn = content.match(/function\s+LikertMatrixCarousel[\s\S]*?(?=\nfunction\s|\nexport\s|$)/);
  return fn?.[0] ?? '';
}

/** Carousel shows stmt + inline radios but no scale-column header row (Q13). */
function likertCarouselLacksScaleHeader(content: string): boolean {
  const block = extractLikertCarouselBlock(content);
  const searchBlock =
    block ||
    (content.match(/gridLayout\s*===\s*['"]carousel['"][\\s\\S]{0,4000}/)?.[0] ?? '');
  if (!searchBlock) return false;
  if (/renderCarouselHeader|CarouselHeaderRow|renderHeaderRow|HeaderRow/.test(searchBlock)) {
    return false;
  }
  if (!/scaleColumns\.map/.test(searchBlock)) return true;
  const stmtIdx = searchBlock.search(/statementText|row\.statementText/);
  const firstMap = searchBlock.search(/scaleColumns\.map/);
  if (stmtIdx >= 0 && firstMap >= 0 && firstMap < stmtIdx) {
    return !/col\.label|\.label/.test(searchBlock.slice(firstMap, stmtIdx + 300));
  }
  return stmtIdx < 0 || firstMap > stmtIdx;
}

/** FAIL when heatmap click handler lacks maxClicksAllowed guard. */
export function scanHeatmapMaxClicksIssues(projectRoot: string): string[] {
  const heatmapRel = findFirstExisting(projectRoot, [
    'src/components/HeatmapScale.tsx',
    'components/HeatmapScale.tsx',
  ]);
  if (!heatmapRel && !surveyPlanHasType(projectRoot, 'HEATMAP')) return [];

  if (!heatmapRel) {
    return ['HeatmapScale.tsx missing — survey has HEATMAP — see layout-cookbook/heatmap.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, heatmapRel), 'utf8');
  const issues: string[] = [];
  if (!/maxClicksAllowed/.test(content)) {
    issues.push(`${heatmapRel}: must enforce question.maxClicksAllowed — see layout-cookbook/heatmap.md`);
  } else if (
    !/spots\.length\s*<\s*question\.maxClicksAllowed|maxClicksAllowed.*spots\.length|spots\.length\s*>=\s*question\.maxClicksAllowed/.test(
      content,
    )
  ) {
    issues.push(
      `${heatmapRel}: handleClick must guard new spots with spots.length < question.maxClicksAllowed — see layout-cookbook/heatmap.md`,
    );
  }
  if (/maxClicksAllowed/.test(content) && !/question\.maxClicksAllowed/.test(content)) {
    issues.push(
      `${heatmapRel}: read maxClicksAllowed from question — not hardcoded — see agent-per-type-verification/heatmap.md`,
    );
  }
  return issues;
}

/** FAIL when §3c CFM bipolar has anchors but Likert lacks scaleAnchorLabels header wiring. */
export function scanBipolarAnchorHeaderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsHeader = variants.some(
    (v) =>
      v.type === 'CFM_MATRIX' &&
      /bipolar/i.test(v.statementLayout) &&
      parseAnchorLabelCount(v.scaleAnchorLabelCount) > 0,
  );
  if (!needsHeader) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) {
    return ['LikertMatrixScale.tsx missing — CFM bipolar with anchors in §3c — see cfm_matrix.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const issues: string[] = [];
  if (!/scaleAnchorLabels/.test(content)) {
    issues.push(
      `${likertRel}: §3c bipolar with anchors — render scaleAnchorLabels header row with 25% spacers — see cfm_matrix.md`,
    );
  }
  if (anchorUsesJustifyBetweenWithoutAbsolute(content)) {
    issues.push(
      `${likertRel}: bipolar anchor row must use position:absolute + % — not justify-between — see scale-position-math.md`,
    );
  }
  return issues;
}

/** FAIL when §3c carousel CFM lacks scale-column header inside carousel card. */
export function scanCarouselScaleHeaderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasCfmCarousel = variants.some(
    (v) => v.type === 'CFM_MATRIX' && /carousel/i.test(v.gridLayout),
  );
  if (!hasCfmCarousel) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  if (likertCarouselLacksScaleHeader(content)) {
    return [
      `${likertRel}: CFM carousel (§3c) — render scaleColumns header row inside card before statement — see cfm_matrix.md (Q13)`,
    ];
  }
  return [];
}

/** FAIL when RATING graphics uses flex distribute for labels instead of columnPercent map. */
export function scanRatingGraphicsIndexMapIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasGraphics = variants.some(
    (v) => v.type === 'RATING_MATRIX' && /graphics/i.test(v.displayStyle),
  );
  if (!hasGraphics) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, csatRel), 'utf8');
  const issues: string[] = [];
  const graphicsBlock =
    content.match(/isGraphics[\s\S]{0,6000}/)?.[0] ??
    content.match(/displayStyle\s*===\s*['"]graphics['"][\\s\\S]{0,6000}/)?.[0] ??
    '';

  if (graphicsBlock) {
    if (
      (/justify-around|justify-between|justifyContent:\s*['"]space-between['"]/.test(graphicsBlock) ||
        /justifyContent:\s*['"]space-around['"]/.test(graphicsBlock)) &&
      !/columnPercent|0\.5\s*\/\s*m|translateX\(-50%\)/.test(graphicsBlock)
    ) {
      issues.push(
        `${csatRel}: RATING graphics labels must use columnPercent + absolute % — not flex distribute — see rating_matrix.md (Q22)`,
      );
    }
  }

  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (trackRel) {
    const track = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
    if (/tickLabels[\s\S]{0,400}<span|tickLabels[\s\S]{0,400}<div/.test(track)) {
      issues.push(
        `${trackRel}: tickLabels are tooltip-only — do not render visible label DOM on track — see custom-slider-track.md`,
      );
    }
  }

  return issues;
}

/** FAIL when SLIDER_MATRIX has both anchors and ticks in one header layer. */
export function scanSliderDualHeaderIssues(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'SLIDER_MATRIX')) return [];

  const sliderRel = findFirstExisting(projectRoot, [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
  ]);
  if (!sliderRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, sliderRel), 'utf8');
  const hasAnchors = /scaleAnchorLabels/.test(content);
  const hasTicks = /ticks\.marks|tickValues|renderHeaderRow/.test(content);
  if (!hasAnchors || !hasTicks) return [];

  if (/bottom-0[\s\S]{0,800}top-0|top-0[\s\S]{0,800}bottom-0/.test(content)) {
    return [
      `${sliderRel}: dual header (anchors + ticks) must use two stacked rows — not bottom-0 + top-0 in one layer — see slider_matrix.md (Q11)`,
    ];
  }
  if (/h-10|height:\s*['"]?40|height:\s*40/.test(content) && /scaleAnchorLabels/.test(content) && /ticks/.test(content)) {
    const headerBlock = content.match(/renderHeaderRow[\s\S]{0,2500}/)?.[0] ?? content;
    const rowContainers = (headerBlock.match(/relative|position:\s*['"]relative['"]/g) ?? []).length;
    if (rowContainers < 2 && !/header-stack|tick-row|anchor-row/i.test(headerBlock)) {
      return [
        `${sliderRel}: when both scaleAnchorLabels and ticks.marks — use two header rows (tick badges then anchors) — see slider_matrix.md`,
      ];
    }
  }
  return [];
}

/** FAIL when SLIDER_MATRIX renders tick badges inside row loop track. */
export function scanSliderPerRowTickBadges(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'SLIDER_MATRIX')) return [];

  const sliderRel = findFirstExisting(projectRoot, [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
  ]);
  if (!sliderRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, sliderRel), 'utf8');
  const rowLoop =
    content.match(/statementRows\.map[\s\S]{0,3500}/)?.[0] ??
    content.match(/statementRows\.map[\s\S]{0,3500}/)?.[0] ??
    '';
  if (!rowLoop) return [];

  if (/CustomSliderTrack[\s\S]{0,800}ticks\s*=/.test(rowLoop) || /tickValues\.map[\s\S]{0,400}CustomSliderTrack/.test(rowLoop)) {
    return [
      `${sliderRel}: SLIDER_MATRIX — tick badges belong in common header once, not per-row CustomSliderTrack — see slider_matrix.md (Q11)`,
    ];
  }
  if (/CustomSliderTrack[\s\S]{0,1200}tickLabels[\s\S]{0,400}<span|CustomSliderTrack[\s\S]{0,1200}tickLabels[\s\S]{0,400}<div/.test(rowLoop)) {
    return [
      `${sliderRel}: SLIDER_MATRIX — visible tick label DOM inside row track — use renderCommonTickHeader + trackOnly — see header-label-overlay.md`,
    ];
  }
  return [];
}

/** FAIL when CustomSliderTrack renders tooltip DOM while parent matrix has common header. */
export function scanMatrixTrackTooltipDom(projectRoot: string): string[] {
  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (!trackRel) return [];

  const track = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
  const hasTooltipDom =
    /tooltip|Tooltip/.test(track) &&
    (/<span[\s\S]{0,200}tooltip|tooltip[\s\S]{0,400}<span|<div[\s\S]{0,200}tooltip|tooltip[\s\S]{0,400}<div/i.test(track) ||
      /isHovered|isDragging/.test(track));
  if (!hasTooltipDom) return [];

  const parentFiles = [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ];
  for (const rel of parentFiles) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;
    const parent = fs.readFileSync(abs, 'utf8');
    if (/renderHeaderRow|GraphicsColumnLabelsRow|renderCommonTickHeader|renderCommonHeader/.test(parent)) {
      if (!/hideTooltip/.test(track) && !/hideTooltip/.test(parent)) {
        return [
          `${trackRel}: matrix parent ${rel} has common header — suppress in-track tooltip (hideTooltip) — see header-label-overlay.md`,
        ];
      }
    }
  }
  return [];
}

/** FAIL when pointerLeave clears drag without pointer capture. */
export function scanCustomSliderPointerLeave(projectRoot: string): string[] {
  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (!trackRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
  const hasLeaveDeactivate =
    /onPointerLeave|onMouseLeave/.test(content) &&
    (/setIsDragging\s*\(\s*false\s*\)|setActive\s*\(\s*false\s*\)|setIsHovered\s*\(\s*false\s*\)[\s\S]{0,80}setIsDragging/.test(content));
  const hasCapture = /setPointerCapture|releasePointerCapture/.test(content);
  if (hasLeaveDeactivate && !hasCapture) {
    return [
      `${trackRel}: use pointer capture on range input — do not deactivate drag on pointerLeave alone — see custom-slider-track.md`,
    ];
  }
  return [];
}

/** FAIL when survey needs position helpers but scaleValues.ts lacks them. */
export function scanScaleValuesPositionHelpers(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsDiscrete = variants.some(
    (v) => v.type === 'RATING_MATRIX' && /graphics/i.test(v.displayStyle),
  );
  const needsContinuous =
    surveyPlanHasType(projectRoot, 'SLIDER_MATRIX') ||
    variants.some((v) => v.type === 'CSAT_MATRIX' && /graphics/i.test(v.displayStyle));

  if (!needsDiscrete && !needsContinuous) return [];

  const scaleRel = findFirstExisting(projectRoot, [
    'src/lib/surveyUi/scaleValues.ts',
    'lib/surveyUi/scaleValues.ts',
  ]);
  if (!scaleRel) {
    return ['lib/surveyUi/scaleValues.ts missing — add columnPercent and tickPercent — see scale-position-math.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, scaleRel), 'utf8');
  const issues: string[] = [];
  if (needsDiscrete && !/columnPercent|0\.5\s*\/\s*m/.test(content)) {
    issues.push(`${scaleRel}: RATING/CSAT graphics needs columnPercent — see scale-position-math.md`);
  }
  if (needsContinuous && !/tickPercent|anchorLabelPercentContinuous/.test(content)) {
    issues.push(`${scaleRel}: SLIDER_MATRIX needs tickPercent (and anchorLabelPercentContinuous when anchors) — see scale-position-math.md`);
  }
  return issues;
}

export function scanLayoutFormatIssues(projectRoot: string): string[] {
  return [...scanBipolarAnchorHeaderIssues(projectRoot), ...scanCarouselScaleHeaderIssues(projectRoot)];
}

export function scanInteractionIssues(projectRoot: string): string[] {
  return [
    ...scanLikertDisplayStyleMisuse(projectRoot),
    ...scanCsatStarIconAntiPattern(projectRoot),
    ...scanMcqOtherPickerIssues(projectRoot),
    ...scanHeatmapMaxClicksIssues(projectRoot),
    ...scanRatingGraphicsIndexMapIssues(projectRoot),
    ...scanSliderDualHeaderIssues(projectRoot),
    ...scanSliderPerRowTickBadges(projectRoot),
    ...scanMatrixTrackTooltipDom(projectRoot),
    ...scanCustomSliderPointerLeave(projectRoot),
    ...scanScaleValuesPositionHelpers(projectRoot),
  ];
}

export function scanMcqOtherIssues(projectRoot: string): string[] {
  if (readMcqOtherFlagFromPlan(projectRoot) !== true) return [];

  const questionRel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!questionRel) {
    return [
      'Question.tsx missing — survey has MCQ Other options; author MCQ handler with MCQ_OTHER_TEXT_CHANGE',
    ];
  }

  const content = fs.readFileSync(path.join(projectRoot, questionRel), 'utf8');
  if (/MCQ_OTHER_TEXT_CHANGE|buildMcqOptionLevelKey/.test(content)) return [];

  return [
    `${questionRel}: survey has MCQ Other options — wire MCQ_OTHER_TEXT_CHANGE + buildMcqOptionLevelKey — see 02-radio.md`,
  ];
}

export interface SourceScanResult {
  stubMatches: string[];
  internalSdkImports: string[];
  nextImports: string[];
  introStartIssues: string[];
  hardcodedStyleIssues: string[];
  endPageCtaIssues: string[];
  matrixStatementIssues: string[];
  matrixLayoutIssues: string[];
  mcqOtherIssues: string[];
}

export function scanProjectSource(projectRoot: string): SourceScanResult {
  const stubMatches: string[] = [];
  const internalSdkImports: string[] = [];
  const nextImports: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);

    if (content.includes('not yet implemented')) {
      stubMatches.push(`${rel}: stub placeholder text`);
    }
    if (/@explorer02\/cfm-survey-sdk\/src|@repo\/sdk\/src/.test(content)) {
      internalSdkImports.push(`${rel}: internal SDK import`);
    }
    if (/from\s+['"]next\//.test(content)) {
      nextImports.push(`${rel}: stale next/* import`);
    }
  }

  const introStartIssues = scanIntroStartIssues(projectRoot);
  const hardcodedStyleIssues = scanHardcodedStyleIssues(projectRoot);
  const endPageCtaIssues = scanEndPageCtaIssues(projectRoot);
  const matrixStatementIssues = scanMatrixStatementIssues(projectRoot);
  const matrixLayoutIssues = scanMatrixLayoutIssues(projectRoot);
  const mcqOtherIssues = scanMcqOtherIssues(projectRoot);

  return {
    stubMatches,
    internalSdkImports,
    nextImports,
    introStartIssues,
    hardcodedStyleIssues,
    endPageCtaIssues,
    matrixStatementIssues,
    matrixLayoutIssues,
    mcqOtherIssues,
  };
}
